import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../components/ui/sheet";
import { Plus, PencilSimple, Trash, X, Tag, Wrench, ListChecks } from "@phosphor-icons/react";
import { toast } from "sonner";

function CoversEditor({ serviceType, onClose }) {
  const [lists, setLists] = useState({ inclusions: [], exclusions: [], notes: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/service-details/${encodeURIComponent(serviceType)}`).then((r) =>
      setLists({ inclusions: r.data.inclusions || [], exclusions: r.data.exclusions || [], notes: r.data.notes || [] }));
  }, [serviceType]);
  const addRow = (k) => setLists((l) => ({ ...l, [k]: [...l[k], { text: "", icon: "", order: l[k].length }] }));
  const setRow = (k, i, text) => setLists((l) => { const n = [...l[k]]; n[i] = { ...n[i], text }; return { ...l, [k]: n }; });
  const delRow = (k, i) => setLists((l) => ({ ...l, [k]: l[k].filter((_, x) => x !== i) }));
  const save = async () => {
    setBusy(true);
    try {
      const clean = (arr) => arr.filter((x) => x.text.trim()).map((x, idx) => ({ ...x, order: idx }));
      await api.put(`/service-details/${encodeURIComponent(serviceType)}`, {
        inclusions: clean(lists.inclusions), exclusions: clean(lists.exclusions), notes: clean(lists.notes) });
      toast.success("Saved"); onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const Section = ({ k, label }) => (
    <div>
      <div className="flex items-center justify-between"><Label className="label-caps">{label}</Label>
        <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => addRow(k)} data-testid={`covers-add-${k}`}>+ Add</Button></div>
      <div className="space-y-2 mt-2">
        {lists[k].map((row, i) => (
          <div key={i} className="flex gap-2">
            <Input className="rounded-sm" value={row.text} onChange={(e) => setRow(k, i, e.target.value)} placeholder="Point text" data-testid={`covers-${k}-${i}`} />
            <button className="text-destructive" onClick={() => delRow(k, i)}><Trash size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <Sheet open={true} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="covers-editor">
        <SheetHeader><SheetTitle className="font-heading">What's covered — {serviceType}</SheetTitle></SheetHeader>
        <div className="py-5 space-y-5">
          <Section k="inclusions" label="What's Included" />
          <Section k="exclusions" label="What's Excluded" />
          <Section k="notes" label="Good to Know / Notes" />
        </div>
        <SheetFooter><Button className="rounded-sm" onClick={save} disabled={busy} data-testid="covers-save">Save</Button></SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default function Catalog() {
  const [categories, setCategories] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [types, setTypes] = useState([]);
  const [typeInput, setTypeInput] = useState("");
  const [newServiceType, setNewServiceType] = useState("");
  const [coversFor, setCoversFor] = useState(null);

  const load = useCallback(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/service-types").then((r) => setServiceTypes(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setName(""); setTypes([]); setTypeInput(""); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setName(c.name); setTypes([...c.product_types]); setTypeInput(""); setOpen(true); };

  const addType = () => {
    const t = typeInput.trim();
    if (t && !types.includes(t)) setTypes([...types, t]);
    setTypeInput("");
  };

  const saveCategory = async () => {
    if (!name.trim()) { toast.error("Category name required"); return; }
    try {
      const payload = { name: name.trim(), product_types: types };
      if (editId) await api.put(`/categories/${editId}`, payload);
      else await api.post("/categories", payload);
      toast.success(`Category ${editId ? "updated" : "created"}`);
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const deleteCategory = async (c) => {
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Category deleted");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addServiceType = async () => {
    if (!newServiceType.trim()) return;
    try {
      await api.post("/service-types", { name: newServiceType.trim() });
      setNewServiceType("");
      toast.success("Service type added");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const deleteServiceType = async (s) => {
    try {
      await api.delete(`/service-types/${s.id}`);
      toast.success("Service type deleted");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHeader
        title="Service Catalog"
        subtitle="Manage product categories, types & service types"
        actions={
          <Button data-testid="add-category-btn" onClick={openNew} className="rounded-sm gap-2">
            <Plus size={16} weight="bold" /> Add Category
          </Button>
        }
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <h3 className="font-heading font-semibold flex items-center gap-2"><Tag size={18} weight="bold" /> Product Categories</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="categories-grid">
            {categories.length === 0 && <p className="text-sm text-muted-foreground">No categories yet.</p>}
            {categories.map((c) => (
              <Card key={c.id} className="rounded-sm p-5 border-border" data-testid={`category-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <h4 className="font-heading font-semibold text-lg">{c.name}</h4>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`category-edit-${c.id}`}>
                      <PencilSimple size={15} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteCategory(c)} data-testid={`category-delete-${c.id}`}>
                      <Trash size={15} />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {c.product_types.length === 0 && <span className="text-xs text-muted-foreground">No product types</span>}
                  {c.product_types.map((t) => (
                    <Badge key={t} variant="outline" className="rounded-sm font-normal">{t}</Badge>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-heading font-semibold flex items-center gap-2"><Wrench size={18} weight="bold" /> Service Types</h3>
          <Card className="rounded-sm p-5 border-border">
            <div className="flex gap-2 mb-4">
              <Input data-testid="service-type-input" value={newServiceType} onChange={(e) => setNewServiceType(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addServiceType()} placeholder="e.g. Installation" className="rounded-sm" />
              <Button className="rounded-sm" onClick={addServiceType} data-testid="service-type-add"><Plus size={16} /></Button>
            </div>
            <div className="space-y-2" data-testid="service-types-list">
              {serviceTypes.map((s) => (
                <div key={s.id} className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                  <span className="text-sm font-medium">{s.name}</span>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="What's covered" onClick={() => setCoversFor(s.name)} data-testid={`service-type-covers-${s.id}`}>
                      <ListChecks size={15} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteServiceType(s)} data-testid={`service-type-delete-${s.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="category-form">
          <SheetHeader><SheetTitle className="font-heading">{editId ? "Edit Category" : "New Category"}</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <div>
              <Label className="label-caps">Category Name</Label>
              <Input data-testid="category-name-input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Air Conditioner" className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="label-caps">Product Types</Label>
              <div className="flex gap-2 mt-1.5">
                <Input data-testid="product-type-input" value={typeInput} onChange={(e) => setTypeInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addType())} placeholder="e.g. Split AC" className="rounded-sm" />
                <Button type="button" className="rounded-sm" onClick={addType} data-testid="product-type-add"><Plus size={16} /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {types.map((t) => (
                  <Badge key={t} variant="outline" className="rounded-sm font-normal gap-1">
                    {t}
                    <button onClick={() => setTypes(types.filter((x) => x !== t))} className="hover:text-destructive">
                      <X size={12} />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={saveCategory} data-testid="category-save">Save Category</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      {coversFor && <CoversEditor serviceType={coversFor} onClose={() => setCoversFor(null)} />}
    </>
  );
}
